'use client'
import { Box, Button, Flex, Image, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useTranslations } from 'next-intl'

import ApplePay from '@/assets/icons/apple-pay.svg'
import CreditCard from '@/assets/icons/credit-card.svg'
import GooglePay from '@/assets/icons/google-pay.svg'
import { getCurrencyByCountryCode } from '@/configs/currencies'
import { useSsrContext } from '@/contexts/ssr'
import { getCurrencyFlagUrl } from '@/i18n/country'
import { gotoCdcWebSignupPage } from '@/utils'
import clsx from '@/utils/clsx'

import styles from './index.module.css'

function CreditCardAddItem() {
  const t = useTranslations('tradeWidget')
  return (
    <>
      <CreditCard height={24} fill={'var(--common-white)'} />
      <Stack ml={8} gap={0} flex={1}>
        <Text variant={'body'}>Credit/debit card</Text>
        <Flex>
          <Text variant={'subtitle'} c={'var(--content-secondary)'}>
            {t('instant')} •{' '}
          </Text>
          <Text td="line-through" ml={8} variant={'subtitle'} c={'var(--content-secondary)'}>
            2.99%
          </Text>
          <Text ml={8} variant={'subtitle'} c={'var(--content-success)'}>
            {t('zeroFeeTips')}
          </Text>
        </Flex>
      </Stack>
      <Button variant={'light'} className={styles.payButton} onClick={gotoCdcWebSignupPage}>
        {t('add')}
      </Button>
    </>
  )
}

function DepositItem() {
  const { countryCode } = useSsrContext()
  const t = useTranslations('tradeWidget')
  const currency = getCurrencyByCountryCode(countryCode)

  return (
    <>
      <Image src={getCurrencyFlagUrl(currency)} w={24} h={24} alt={'Crypto.com Country Flag'} />
      <Stack ml={8} gap={0} flex={1}>
        <Text variant={'body'}>
          {currency} {t('account')}
        </Text>
        <Text variant={'subtitle'} c={'var(--content-secondary)'}>
          {t('noFeesTips')}
        </Text>
      </Stack>
      <Button variant={'light'} className={styles.payButton} onClick={gotoCdcWebSignupPage}>
        {t('deposit')}
      </Button>
    </>
  )
}
function GooglePayItem({ isActive }: { isActive: boolean }) {
  const t = useTranslations('tradeWidget')
  return (
    <>
      <GooglePay height={24} />
      <Text variant={'body'} flex={1} ml={8}>
        {t('googlePay')}
      </Text>
      <Box className={isActive ? styles.payWithItemCheckBoxActive : styles.payWithItemCheckBox} />
    </>
  )
}

function ApplePayItem({ isActive }: { isActive: boolean }) {
  const t = useTranslations('tradeWidget')
  return (
    <>
      <ApplePay height={24} />
      <Text variant={'body'} flex={1} ml={8}>
        {t('applePay')}
      </Text>
      <Box className={isActive ? styles.payWithItemCheckBoxActive : styles.payWithItemCheckBox} />
    </>
  )
}

export default function PayWith() {
  const [isGooglePay, { open, close }] = useDisclosure(true)
  const t = useTranslations('tradeWidget')
  const { countryCode } = useSsrContext()
  const items = [
    {
      content: <GooglePayItem key={'goolePayItem'} isActive={isGooglePay} />,
      onClick: open,
    },
    {
      content: <ApplePayItem key={'applePayItem'} isActive={!isGooglePay} />,
      onClick: close,
    },
    // Deposit and credit card is not support for KR
    ...(countryCode === 'KR'
      ? []
      : [
          {
            content: <DepositItem key={'depositItem'} />,
          },
          {
            content: <CreditCardAddItem key={'creditCardItem'} />,
          },
        ]),
  ]
  return (
    <Stack gap={12}>
      <Text variant={'body-emphasis'}>{t('payWith')}</Text>
      <Stack gap={0} className={styles.payWith}>
        {items.map(({ content, onClick }, index) => (
          <Flex
            className={clsx({
              [styles.payWithItem]: true,
              [styles.payWithItemClickable]: !!onClick,
            })}
            key={index}
            onClick={onClick}
          >
            {content}
          </Flex>
        ))}
      </Stack>
    </Stack>
  )
}
